import { useEffect, useMemo, useState } from 'react';
import { GenericDialog } from 'src/shared/components/organisms/genericDialog';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Checkbox,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { InfoOutlined, Undo } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import LoadingOverlay from 'src/shared/components/organisms/loadingOverlay';
import { DateHelper } from 'src/shared/helpers/dateHelper';
import { formatDate } from 'src/shared/functions/formatDate';
import { getNextExpireDatePreview, markMembersAsPayed } from 'src/shared/services/memberService';
import { Member } from '../models/member';

type MemberMarkAsPayedModalProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
  members: Member[];
  onSaved: () => void;
};

// Massregistrering av betalt medlemskap, motsvarande Förfallolistan i 1.0. Ett gemensamt
// betaldatum styr alla rader, men varje medlem kan få ett eget datum och kan kryssas bort
// precis som i 1.0. Listan visar vilket förfallodatum varje medlem får, så att regeln inte
// behöver beskrivas i ord.
export const MemberMarkAsPayedModal = (props: MemberMarkAsPayedModalProps) => {
  const { open, setOpen, members, onSaved } = props;
  const { t } = useTranslation(['member', 'common']);

  const [loading, setLoading] = useState<boolean>(false);
  const [payDate, setPayDate] = useState<string>(DateHelper.getDate(new Date()));

  // Bara de rader användaren faktiskt ändrat ligger här. Övriga följer det gemensamma
  // datumet, så att en ändring högst upp flyttar hela listan.
  const [rowDates, setRowDates] = useState<Record<number, string>>({});

  // Urvalet är redan gjort i medlemsregistret, så alla rader är med från början. Listan är
  // en sista avstämning där enskilda medlemmar kan kryssas bort.
  const [excluded, setExcluded] = useState<number[]>([]);

  const [preview, setPreview] = useState<Record<number, string>>({});

  const rows = useMemo(() => members.filter((x) => (x.member_id || 0) > 0), [members]);

  // Medlemmar i föreningar som fakturerar förlängs av betalningen på fakturan. De visas kvar
  // i listan så att urvalet går att känna igen, men utan kryssruta och utan datumfält,
  // eftersom ingenting av det skulle sparas.
  const isOcr = (member: Member) => !!member.association?.ocr;

  const payableRows = useMemo(() => rows.filter((x) => !x.association?.ocr), [rows]);

  const ocrCount = rows.length - payableRows.length;

  const getRowDate = (memberId: number) => rowDates[memberId] || payDate;

  const isIncluded = (memberId: number) => !excluded.includes(memberId);

  const includedRows = payableRows.filter((x) => isIncluded(x.member_id || 0));

  const newMemberCount = includedRows.filter((x) => !x.admDate).length;

  const reenteredCount = includedRows.filter((x) => !!x.resDate && x.resCause !== 'Avliden').length;

  const toggleRow = (memberId: number) => {
    setExcluded((prev) => (prev.includes(memberId) ? prev.filter((x) => x !== memberId) : [...prev, memberId]));
  };

  const toggleAll = () => {
    setExcluded(includedRows.length === 0 ? [] : payableRows.map((x) => x.member_id || 0));
  };

  // Som i 1.0: skriver man ett eget datum på en rad kryssas den i, så att ingen anger ett
  // datum som sedan inte sparas.
  const setRowDate = (memberId: number, date: string) => {
    setRowDates((prev) => ({ ...prev, [memberId]: date }));
    setExcluded((prev) => prev.filter((x) => x !== memberId));
  };

  // Förhandsvisningen hämtas från samma kod som sparar. Den fördröjs lite, annars blir det
  // ett anrop per tangenttryckning i datumfälten.
  useEffect(() => {
    const memberIds = payableRows.map((x) => x.member_id || 0);

    if (memberIds.length === 0) {
      setPreview({});
      return undefined;
    }

    const timer = setTimeout(async () => {
      try {
        const res = await getNextExpireDatePreview(
          memberIds,
          payDate,
          memberIds.filter((id) => !!rowDates[id]).map((id) => ({ memberId: id, payDate: rowDates[id] })),
        );

        const next: Record<number, string> = {};

        res.data.forEach((item) => {
          next[item.memberId] = item.nextExpireDate;
        });

        setPreview(next);
      } catch (e) {
        ConsoleHelper.log(e);
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [payableRows, payDate, rowDates]);

  const payDateHelp = (
    <Box>
      {['payment', 'expireDate'].map((key) => (
        <Typography key={key} variant='body2' sx={{ mb: 1, '&:last-child': { mb: 0 } }}>
          {t(`member:markAsPayed.payDateHelp.${key}`)}
        </Typography>
      ))}
    </Box>
  );

  const onSave = async () => {
    if (includedRows.length === 0) {
      toast.warning(t('member:markAsPayed.noneSelected') as string);
      return;
    }

    setLoading(true);

    try {
      const memberIds = includedRows.map((x) => x.member_id || 0);

      const res = await markMembersAsPayed(
        memberIds,
        payDate,
        memberIds.filter((id) => !!rowDates[id]).map((id) => ({ memberId: id, payDate: rowDates[id] })),
      );

      // API:et hoppar över medlemmar vi inte skulle ha skickat, och det ska sägas rakt ut,
      // annars ser en halv körning ut som en lyckad.
      const skipped = res.data.skippedOcr + res.data.skippedNoAccess;

      if (skipped > 0) {
        toast.warning(t('member:markAsPayed.partial', { updated: res.data.updated, skipped }) as string);
      } else {
        toast.success(t('member:markAsPayed.success', { count: res.data.updated }) as string);
      }

      setOpen(false);
      onSaved();
    } catch (e) {
      ConsoleHelper.log(e);
      toast.error(t('member:markAsPayed.error') as string);
    } finally {
      setLoading(false);
    }
  };

  const onHandleClose = () => {
    setRowDates({});
    setExcluded([]);
    setOpen(false);
  };

  return (
    <GenericDialog
      dialogTitle={t('member:markAsPayed.title')}
      open={open}
      maxWidth='md'
      showDialogActions
      dialogContent={
        <Grid container spacing={2}>
          <LoadingOverlay loading={loading} />
          <Grid item xs={12}>
            <Typography variant='body2'>{t('member:markAsPayed.info', { count: includedRows.length })}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <DatePicker
                format='YYYY-MM-DD'
                label={t('member:markAsPayed.payDate')}
                value={dayjs(payDate)}
                onChange={(date) => {
                  if (date && date.isValid()) setPayDate(date.format('YYYY-MM-DD'));
                }}
                slots={{
                  textField: (params) => <TextField {...params} size='small' InputLabelProps={{ shrink: true }} fullWidth />,
                }}
              />
              <Tooltip title={payDateHelp} arrow componentsProps={{ tooltip: { sx: { maxWidth: 420 } } }}>
                <InfoOutlined sx={{ ml: 1, color: 'text.disabled', cursor: 'default', opacity: 0.9 }} />
              </Tooltip>
            </Box>
          </Grid>
          {ocrCount > 0 && (
            <Grid item xs={12}>
              <Alert severity='info'>{t('member:markAsPayed.ocrInfo', { count: ocrCount })}</Alert>
            </Grid>
          )}
          {newMemberCount > 0 && (
            <Grid item xs={12}>
              <Alert severity='info'>{t('member:markAsPayed.newMembers', { count: newMemberCount })}</Alert>
            </Grid>
          )}
          {reenteredCount > 0 && (
            <Grid item xs={12}>
              <Alert severity='info'>{t('member:markAsPayed.reenteredMembers', { count: reenteredCount })}</Alert>
            </Grid>
          )}
          <Grid item xs={12}>
            <Box sx={{ maxHeight: 360, overflowY: 'auto' }}>
              <Table size='small' stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell padding='checkbox'>
                      <Tooltip title={t('member:markAsPayed.toggleAll')}>
                        <Checkbox
                          checked={includedRows.length === payableRows.length && payableRows.length > 0}
                          indeterminate={includedRows.length > 0 && includedRows.length < payableRows.length}
                          disabled={payableRows.length === 0}
                          onChange={toggleAll}
                        />
                      </Tooltip>
                    </TableCell>
                    <TableCell>{t('member:tableHeaders.member_id')}</TableCell>
                    <TableCell>{t('member:tableHeaders.name')}</TableCell>
                    <TableCell>{t('member:tableHeaders.association')}</TableCell>
                    <TableCell>{t('member:tableHeaders.expireDate')}</TableCell>
                    <TableCell sx={{ width: 200 }}>{t('member:markAsPayed.payDate')}</TableCell>
                    <TableCell>{t('member:markAsPayed.nextExpireDate')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((member) => {
                    const memberId = member.member_id || 0;
                    const ocr = isOcr(member);
                    const overridden = !!rowDates[memberId];
                    const included = !ocr && isIncluded(memberId);
                    const nextExpireDate = preview[memberId];

                    return (
                      <TableRow key={memberId} sx={{ opacity: included ? 1 : 0.5 }}>
                        <TableCell padding='checkbox'>
                          <Checkbox checked={included} disabled={ocr} onChange={() => toggleRow(memberId)} />
                        </TableCell>
                        <TableCell>{member.memberNr}</TableCell>
                        <TableCell>{`${member.firstName} ${member.lastName}`}</TableCell>
                        <TableCell>{member.association?.localAss}</TableCell>
                        <TableCell>{formatDate(member.expireDate)}</TableCell>
                        <TableCell>
                          {ocr ? (
                            <Typography variant='body2' color='text.secondary'>
                              {t('member:markAsPayed.ocrRow')}
                            </Typography>
                          ) : (
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <DatePicker
                                format='YYYY-MM-DD'
                                value={dayjs(getRowDate(memberId))}
                                onChange={(date) => {
                                  if (date && date.isValid()) setRowDate(memberId, date.format('YYYY-MM-DD'));
                                }}
                                slots={{
                                  textField: (params) => <TextField {...params} size='small' fullWidth />,
                                }}
                              />
                              {overridden && (
                                <Tooltip title={t('member:markAsPayed.resetRow')}>
                                  <IconButton
                                    size='small'
                                    onClick={() =>
                                      setRowDates((prev) => {
                                        const next = { ...prev };
                                        delete next[memberId];
                                        return next;
                                      })
                                    }
                                  >
                                    <Undo fontSize='small' />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                          )}
                        </TableCell>
                        <TableCell>
                          {!ocr && nextExpireDate && nextExpireDate === member.expireDate && (
                            <Typography variant='body2' color='text.secondary'>
                              {t('member:markAsPayed.unchanged')}
                            </Typography>
                          )}
                          {!ocr && nextExpireDate && nextExpireDate !== member.expireDate && (
                            <Typography variant='body2' sx={{ fontWeight: 600 }}>
                              {formatDate(nextExpireDate)}
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          </Grid>
        </Grid>
      }
      onClose={onHandleClose}
      onSave={onSave}
    />
  );
};
